import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import {
  DatalistField,
  FormSelectField,
  FormTextField,
} from "../../pages/ReferralInbox/components/ReferralDataForm/components/FormComponents";
import { STATES_OPTIONS } from "../../constants";

const buildClinicLabel = (clinicRecord, hasDuplicateName) =>
  hasDuplicateName
    ? `${clinicRecord.name} (${clinicRecord.suburb || clinicRecord.postCode || clinicRecord.id})`
    : clinicRecord.name;

const ClinicForPatientForm = ({ clinic, handleValueChange, updateResource }) => {
  const clinics = useSelector(({ app: { clinics } }) => clinics);
  const [clinicOptions, setClinicOptions] = useState([]);

  const labelledClinics = useMemo(() => {
    const nameCounts = clinics.reduce((counts, { name }) => {
      const key = (name || "").toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {});
    return clinics.map((clinicRecord) => ({
      label: buildClinicLabel(clinicRecord, nameCounts[(clinicRecord.name || "").toLowerCase()] > 1),
      clinic: clinicRecord,
    }));
  }, [clinics]);

  const applyClinic = (selectedClinic) => {
    updateResource({
      id: selectedClinic.id,
      name: selectedClinic.name,
      street: selectedClinic.street,
      suburb: selectedClinic.suburb,
      postCode: selectedClinic.postCode,
      state: selectedClinic.state,
      email: selectedClinic.email,
      phone: selectedClinic.phone,
      fax: selectedClinic.fax,
    });
  };

  const resetResource = () =>
    updateResource({
      id: "",
      name: "",
      street: "",
      suburb: "",
      postCode: "",
      email: "",
      phone: "",
      fax: "",
    });

  const onClinicSelected = (selectedItem) => {
    const match = labelledClinics.find(({ label }) => label === selectedItem);
    if (!match) {
      return;
    }
    applyClinic(match.clinic);
  };

  const handleClinicValueChange = (value) => {
    resetResource();
    const needle = value.trim().toLowerCase();
    setClinicOptions(
      labelledClinics.filter(({ label }) => label.toLowerCase().includes(needle)).map(({ label }) => label)
    );
  };

  useEffect(() => {
    if (_.isEmpty(clinics) || _.isEmpty(clinic.name)) {
      return;
    }
    if (clinic.id) {
      const bound = clinics.find(({ id }) => id === clinic.id);
      if (bound) {
        applyClinic(bound);
      }
      return;
    }
    const matches = clinics.filter(({ name }) => name?.toLowerCase() === clinic.name.toLowerCase());
    if (matches.length === 1) {
      applyClinic(matches[0]);
    }
    // eslint-disable-next-line
  }, [clinics]);

  useEffect(() => {
    setClinicOptions(labelledClinics.map(({ label }) => label));
  }, [labelledClinics]);

  return (
    <>
      <p className="menu-label">CLINIC</p>
      <div className="columns">
        <DatalistField
          defaultValue={clinic.name}
          id="clinic"
          className="column is-half"
          options={clinicOptions}
          onSelect={onClinicSelected}
          onValueChange={handleClinicValueChange}
        />
      </div>
      <div className="columns">
        <FormTextField
          className="column is-full"
          placeholder="Clinic Name"
          value={clinic.name}
          onValueChange={handleValueChange("name")}
        />
      </div>
      <div className="columns">
        <FormTextField
          className="column"
          placeholder="Address"
          value={clinic.street}
          onValueChange={handleValueChange("street")}
        />
        <FormSelectField
          onValueChange={handleValueChange("state")}
          options={STATES_OPTIONS}
          className="column"
          value={clinic.state}
        />
      </div>
      <div className="columns">
        <FormTextField
          className="column is-half"
          placeholder="Suburb"
          value={clinic.suburb}
          onValueChange={handleValueChange("suburb")}
        />
        <FormTextField
          value={clinic.postCode}
          className="column is-half"
          placeholder="Postcode"
          onValueChange={handleValueChange("postCode")}
        />
      </div>
      <div className="columns">
        <FormTextField
          className="column is-half"
          placeholder="Email"
          value={clinic.email}
          onValueChange={handleValueChange("email")}
        />
        <FormTextField
          className="column is-one-quarter"
          placeholder="Phone"
          value={clinic.phone}
          onValueChange={handleValueChange("phone")}
        />
        <FormTextField
          className="column is-one-quarter"
          placeholder="Fax"
          value={clinic.fax}
          onValueChange={handleValueChange("fax")}
        />
      </div>
    </>
  );
};

export default ClinicForPatientForm;
